/**
 * Search Input Component
 */

import React from 'react'
import {
	InputBase,
	Paper,
	Typography,
	CircularProgress,
	List,
	Popover,
	Popper,
	ListItem,
	ListItemText,
	Box,
	ClickAwayListener,
	Backdrop,
	ListItemIcon,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/SearchOutlined'
import { SearchItem } from '@/types/search'
import { useLocation, useNavigate } from 'react-router-dom'
/** ICONS */
import CustomerIcon from '@mui/icons-material/PersonOutline'
import InvoiceIcon from '@mui/icons-material/Receipt'
import ContractIcon from '@mui/icons-material/Description'
import ProductIcon from '@mui/icons-material/LocalMall'


type SearchProps = {
	placeholder: string
	width: 'full' | 'half' | 'large'
}

const Search = (props: SearchProps) => {
	const [width, setWidth] = React.useState<string>('100%')
	const [search, setSearch] = React.useState<string>('')
	const [isLoading, setIsLoading] = React.useState<boolean>(false)

	const [items, setItems] = React.useState<SearchItem[]>([])

	const searchRef = React.useRef<HTMLInputElement>(null)

	const location = useLocation()
	const navigate = useNavigate()

	React.useEffect(() => {
		if (props.width === 'half') {
			setWidth('50%')
		} else if (props.width === 'large') {
			setWidth('75%')
		} else {
			setWidth('100%')
		}
	}, [props.width])

	React.useEffect(() => {
		// listen to ctrl+f or cmd+f
		const handleKeyPress = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && e.key === 'f' && searchRef.current) {
				e.preventDefault()
				e.stopPropagation()

				const inputElement = searchRef.current.querySelector('input')

				if (inputElement) {
					inputElement.focus()
				}
			}
		}

		window.addEventListener('keydown', handleKeyPress)

		return () => {
			window.removeEventListener('keydown', handleKeyPress)
		}
	}, [])

	React.useEffect(() => {
		if (search.length === 0) {
			return
		}
		// debounced with 300ms
		const timeout = setTimeout(() => {
			setIsLoading(true)

			fetch(`${process.env.REACT_APP_API_URL}/search?q=${search}`)
				.then((response) => response.json())
				.then((data) => {
					setItems(data.data)
					setIsLoading(false)
				})
				.catch((error) => {
					setIsLoading(false)
				})
		}, 300)

		return () => {
			clearTimeout(timeout)
		}
	}, [search])

	return (
		<Box
			sx={{
				position: 'relative',
				width: width,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
			}}
		>
			<Paper
				elevation={0}
				variant="outlined"
				sx={{
					display: 'flex',
					alignItems: 'center',
					p: 1,
					width: '100%',
					boxShadow: 'none',
					height: '100%',
					zIndex: items.length > 0 ? 9999 : 1,
				}}
			>
				{isLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon fontSize="small" />}
				<InputBase
					placeholder={props.placeholder ?? 'Хайх'}
					sx={{
						ml: 1,
						fontSize: 13,
						p: 0,
						// disable padding
						'& .MuiInputBase-input': {
							p: 0,
						},
					}}
					fullWidth
					ref={searchRef}
					size="small"
					tabIndex={0}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
				<Typography variant="caption" color={'GrayText'} fontSize={11}>
					⌘+F
				</Typography>
			</Paper>
			<ClickAwayListener onClickAway={() => setItems([])}>
				<Popper
					open={items.length > 0}
					anchorEl={searchRef.current}
					placement="bottom"
					style={{
						//backgroundColor: 'paper.default',
						width: searchRef.current ? searchRef.current.clientWidth + 50 : 'auto',
						top: 15,
						boxShadow: '0 0 10px rgba(0,0,0,0.1)',
						zIndex: 9999,
					}}
					modifiers={[
						{
							name: 'offset',
							options: {
								offset: [0, 10],
							},
						},
						{
							name: 'arrow',
							options: {
								element: searchRef.current,
							},
						},
					]}
				>
					<Paper variant="outlined" sx={{
						width: searchRef.current ? searchRef.current.clientWidth + 50 : 'auto',
					}}>
						<List>
							{items.map((item, index) => (
								<ListItem
									key={index}
									dense
									divider
									onClick={() => {
										navigate(item?.url ?? '#')
										setItems([])
										setSearch('')
									}}
									sx={{
										'&:hover': {
											backgroundColor: 'rgba(0,0,0,0.05)',
											cursor: 'pointer',
										},
									}}
								>
									<ListItemIcon>
										{item.type === 'customer' && <CustomerIcon />}
										{item.type === 'invoice' && <InvoiceIcon />}
										{item.type === 'contract' && <ContractIcon />}
										{item.type === 'product' && <ProductIcon />}
									</ListItemIcon>
									<ListItemText primary={item.title} secondary={item?.subtitle ?? ''} />
								</ListItem>
							))}
						</List>
					</Paper>
				</Popper>
			</ClickAwayListener>
			<Backdrop open={items.length > 0} sx={{ zIndex: 9998 }}>
				
			</Backdrop>
		</Box>
	)
}

export default Search
